import {
  faCircleInfo,
  faMapMarkerAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion'; // Importamos framer-motion para las animaciones
import { debounce } from 'lodash';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import marcadorPersonalizado from '../../assets/isotipo.png';
import positionPin from '../../assets/position_pin.png';
import { mapStyles } from '../../utils/mapStyles';
import TarjetaPropiedadHorizontal from '../cards/TarjetaPropiedadHorizontal';
import MarkerCluster from './MarkerCluster';

const MapaGoogle = forwardRef(
  (
    {
      datosPropiedades,
      crearIconoMarcador,
      setZonaVisible,
      ubicacionBusqueda,
      expanded,
      tipoRenta,
      buscando,
      onDatosCambiados,
    },
    ref
  ) => {
    const mapaRef = useRef();
    const geocoderRef = useRef();
    const [mapa, setMapa] = useState(null);
    const [marcadores, setMarcadores] = useState([]);
    const [poligono, setPoligono] = useState(null);
    const [marcadorBusqueda, setMarcadorBusqueda] = useState(null);
    const [numResultados, setNumResultados] = useState(0);
    const [propiedadSeleccionada, setPropiedadSeleccionadaLocal] =
      useState(null);
    const [marcadorSeleccionado, setMarcadorSeleccionado] = useState(null);

    const redondearEtiqueta = (etiqueta) => {
      if (!etiqueta) return '';
      const numero = parseFloat(etiqueta);

      if (numero >= 1000) {
        const valorFormateado = (numero / 1000).toFixed(1).replace(/\.0$/, '');
        return `$${valorFormateado}K`;
      } else {
        return `$${numero.toFixed(1).replace(/\.0$/, '')}`;
      }
    };

    const manejarCerrarPropiedad = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (marcadorSeleccionado) {
        const etiqueta = marcadorSeleccionado.get('etiqueta');
        const esDestacado = marcadorSeleccionado.get('es_destacado');
        marcadorSeleccionado.setIcon(crearIconoMarcador(etiqueta, esDestacado));
        setMarcadorSeleccionado(null);
      }
      setPropiedadSeleccionadaLocal(null);
    };

    useImperativeHandle(ref, () => ({
      limpiarPoligonosYMarcadores() {
        if (poligono) {
          poligono.setMap(null);
          setPoligono(null);
        }
        if (marcadorBusqueda) {
          marcadorBusqueda.setMap(null);
          setMarcadorBusqueda(null);
        }
      },
      actualizarMarcadores() {
        if (marcadores.length > 0) {
          marcadores.forEach((marcador) => marcador.setMap(null)); // Eliminar marcadores existentes
        }

        const nuevosMarcadores = datosPropiedades
          .map((data) => {
            if (!data.location || !data.location.lat || !data.location.lng) {
              console.error('Datos de ubicación faltantes:', data);
              return null;
            }

            // Determinamos la etiqueta correcta según el tipo de renta
            const etiqueta =
              data.monthly_rent === 0
                ? data.daily_price
                : tipoRenta.includes(4) && data.monthly_rent !== 0
                  ? data.monthly_rent
                  : tipoRenta.some((renta) => [1, 2, 3].includes(renta)) &&
                      !tipoRenta.includes(4)
                    ? data.daily_price
                    : tipoRenta.some((renta) => [1, 2, 3].includes(renta)) &&
                        tipoRenta.includes(4) &&
                        data.monthly_rent !== 0
                      ? data.monthly_rent
                      : data.monthly_rent;

            const etiquetaRedondeada = redondearEtiqueta(etiqueta);

            const marcador = new window.google.maps.Marker({
              position: { lat: data.location.lat, lng: data.location.lng },
              label: '',
              icon: crearIconoMarcador(etiquetaRedondeada, data.is_highlighted),
              map: mapa,
              title: `Marcador de propiedad`,
            });

            marcador.set('etiqueta', etiquetaRedondeada);
            marcador.set('es_destacado', data.is_highlighted);

            marcador.addListener('click', () => {
              setMarcadorSeleccionado((prev) => {
                if (prev && prev !== marcador) {
                  const etiquetaAnterior = prev.get('etiqueta');
                  const esDestacadoAnterior = prev.get('es_destacado');
                  prev.setIcon(
                    crearIconoMarcador(etiquetaAnterior, esDestacadoAnterior)
                  );
                }
                marcador.setIcon({
                  url: marcadorPersonalizado,
                  scaledSize: new window.google.maps.Size(35, 40),
                });
                return marcador;
              });
              setPropiedadSeleccionadaLocal(data);
            });

            return marcador;
          })
          .filter((marcador) => marcador !== null);

        setMarcadores(nuevosMarcadores);
        setNumResultados(nuevosMarcadores.length);
      },
    }));

    useEffect(() => {
      if (window.google && !geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
    }, []);

    useEffect(() => {
      if (!window.google.maps.Map || mapa) return;

      const map = new window.google.maps.Map(mapaRef.current, {
        center: { lat: 20.72356, lng: -103.38479 },
        zoom: 13,
        styles: mapStyles,
        gestureHandling: 'greedy',
      });

      setMapa(map);

      const handleBoundsChanged = debounce(() => {
        const bounds = map.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        const nuevaZona = {
          latMin: sw.lat(),
          latMax: ne.lat(),
          lngMin: sw.lng(),
          lngMax: ne.lng(),
        };
        setZonaVisible(nuevaZona);

        const center = map.getCenter();
        // obtenerDireccion(center);
      }, 500);

      map.addListener('idle', handleBoundsChanged);
    }, []);

    useEffect(() => {
      if (mapa && datosPropiedades) {
        ref.current.actualizarMarcadores();
      }
    }, [datosPropiedades, mapa]);

    const obtenerDireccion = (location) => {
      console.log('llega aqui y que es geocoder', geocoderRef.current);
      if (!geocoderRef.current) return;

      geocoderRef.current.geocode({ location }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
          const place = results[0];
          console.log('Lugar (Mapa):', place);
          console.log('address components', place.address_components);

          if (onDatosCambiados) {
            onDatosCambiados({
              direccion: place.formatted_address,
              coordenadas: location,
              addressComponents: place.address_components,
            });
          }
        } else {
          console.error('Error al obtener la dirección:', status);
        }
      });
    };

    useEffect(() => {
      if (mapa && ubicacionBusqueda) {
        const service = new window.google.maps.places.PlacesService(mapa);

        if (ubicacionBusqueda.place_id) {
          service.getDetails(
            { placeId: ubicacionBusqueda.place_id },
            (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                if (place.geometry.viewport) {
                  mapa.fitBounds(place.geometry.viewport);
                } else {
                  mapa.setCenter(place.geometry.location);
                  mapa.setZoom(17);
                }

                if (poligono) {
                  poligono.setMap(null);
                  setPoligono(null);
                }
                if (marcadorBusqueda) {
                  marcadorBusqueda.setMap(null);
                  setMarcadorBusqueda(null);
                }

                if (
                  place.types.includes('neighborhood') ||
                  place.types.includes('locality') ||
                  place.types.includes('political')
                ) {
                  obtenerPoligonoColonia(place.name).then((paths) => {
                    if (paths.length > 0) {
                      const newPolygon = new window.google.maps.Polygon({
                        paths,
                        strokeColor: '#4D368A',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#4D368A',
                        fillOpacity: 0.1,
                      });
                      newPolygon.setMap(mapa);
                      setPoligono(newPolygon);
                    }
                  });
                } else {
                  const newMarker = new window.google.maps.Marker({
                    position: place.geometry.location,
                    map: mapa,
                    icon: {
                      url: marcadorPersonalizado,
                      scaledSize: new window.google.maps.Size(45, 50),
                    },
                  });
                  setMarcadorBusqueda(newMarker);
                }
              }
            }
          );
        } else if (ubicacionBusqueda.geometry) {
          mapa.setCenter(ubicacionBusqueda.geometry.location);
          mapa.setZoom(17);

          if (marcadorBusqueda) {
            marcadorBusqueda.setMap(null);
          }

          const newMarker = new window.google.maps.Marker({
            position: ubicacionBusqueda.geometry.location,
            map: mapa,
            icon: {
              url: marcadorPersonalizado,
              scaledSize: new window.google.maps.Size(50, 50),
            },
          });
          setMarcadorBusqueda(newMarker);
        }
      }
    }, [mapa, ubicacionBusqueda]);

    const manejarUbicacionActual = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          const newMarker = new window.google.maps.Marker({
            position: location,
            map: mapa,
            icon: {
              url: positionPin,
              scaledSize: new window.google.maps.Size(50, 50),
            },
          });
          mapa.setCenter(location);
          mapa.setZoom(17);
          setMarcadorBusqueda(newMarker);
        });
      }
    };

    // Animaciones de burbujas flotantes
    const BubblesAnimation = () => (
      <div className="relative flex justify-center items-center w-full">
        {[...Array(5)].map((_, index) => (
          <motion.div
            key={index}
            className="bubble"
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 1, 0], y: [-10, -50, -100] }}
            transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
            style={{ left: `${Math.random() * 100}%` }} // Las burbujas aparecen en diferentes puntos del botón
          />
        ))}
        <style jsx="true">{`
          .bubble {
            position: absolute;
            width: 10px;
            height: 10px;
            background-color: #06b6d4;
            border-radius: 50%;
            bottom: 0;
          }
        `}</style>
      </div>
    );

    return (
      <div className="relative w-full h-full">
        <div ref={mapaRef} className="w-full h-full contenedor_mapa" />
        {
          mapa &&
            marcadores.length >
              0 /*&& <MarkerCluster map={mapa} markers={marcadores} />  DE MOMENTO OCULTAMOS EL MARKERCLUSTER PARA QUE APAREZCAN UNICAMENTE LAS ETIQUETAS*/
        }

        <div
          className={`md:p-5 p-2 w-full ${expanded ? 'md:w-full pr-[5rem]' : 'md:w-6/12'} text-sm absolute bottom-0 left-0 flex flex-col items-start md:space-y-4 space-y-2 contenedor_absoluto_maps`}
        >
          <div className="flex flex-col md:flex-row items-start md:space-x-4 gap-2">
            <button
              onClick={manejarUbicacionActual}
              className="bg-purple-600 text-white p-4 rounded-full shadow-lg flex items-center justify-center hover:bg-purple-700 transition duration-300"
              aria-label="Mostrar mi ubicación actual"
            >
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="w-5 h-5 text-white mr-2"
              />{' '}
              ¿Dónde estoy?
            </button>

            <div className="relative bg-sky-400 text-white p-3 px-5 rounded-full shadow-lg flex flex-col justify-center items-center hover:bg-sky-500 transition duration-300">
              {buscando ? (
                <>
                  <BubblesAnimation />
                  <div className="flex flex-nowrap flex-row">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="w-5 h-5 text-white"
                    />
                    <span className="ml-2 font-bold">
                      Buscando...<br></br>Esto puede tardar un momento.
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-nowrap flex-row">
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="w-5 h-5 text-white"
                    />
                    <span className="ml-2 font-bold">
                      {numResultados} resultados
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {propiedadSeleccionada && (
            <TarjetaPropiedadHorizontal
              key={propiedadSeleccionada.id}
              propiedad={propiedadSeleccionada}
              onRemove={manejarCerrarPropiedad}
            />
          )}
        </div>
      </div>
    );
  }
);

export default MapaGoogle;
